// Who is away today and whose leave starts soon — read from the Leave Calendar entries
// (roster-data). One answer for the Dashboard banner and the 6am push (leave-alerts), so
// the office and the team leaders can never be told different things.
//
// Why it exists: 26 Oct 2026 Pablo (Broadway) had approved leave that day and nobody
// remembered until someone happened to open the calendar. Approved leave was only ever
// visible if you went looking.
//
// CommonJS + no spread / for…of, like leaveRules.js and atoPlan.js: webpack imports it into
// the Dashboard and netlify/functions/leave-alerts.js requires it directly.

var DAY = 86400000;

function isoOf(d) {
  return d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0') + '-' + String(d.getUTCDate()).padStart(2, '0');
}
function daysBetween(fromIso, toIso) {
  return Math.round((Date.parse(toIso + 'T00:00:00Z') - Date.parse(fromIso + 'T00:00:00Z')) / DAY);
}
function addDays(iso, n) {
  return isoOf(new Date(Date.parse(iso + 'T00:00:00Z') + n * DAY));
}

// An entry's team(s) — a few are booked across two ("City,Broadway").
function teamsOf(e) {
  return String((e && e.team) || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean);
}

var TYPE_LABEL = { annual: 'holiday', exception: 'holiday', sick: 'sick' };

function row(e, today) {
  return {
    id: e.id,
    name: (e.name || e.login || '').trim(),
    login: (e.login || '').toUpperCase(),
    teams: teamsOf(e),
    type: TYPE_LABEL[e.type] || e.type || 'leave',
    status: e.status,
    startDate: e.startDate,
    endDate: e.endDate || e.startDate,
    startsIn: daysBetween(today, e.startDate),
    days: daysBetween(e.startDate, e.endDate || e.startDate) + 1,
  };
}

// entries: Leave Calendar entries. today: 'YYYY-MM-DD' (Sydney).
// opts.ahead: how many days forward counts as "upcoming" (default 14).
// opts.team: keep only entries in this team (a team leader's view).
// → { away: approved leave covering today,
//     upcoming: approved leave starting within `ahead` days,
//     pending: requests still awaiting a decision that start within `ahead` days or are under way }
function leaveOverview(entries, today, opts) {
  opts = opts || {};
  var ahead = opts.ahead == null ? 14 : opts.ahead;
  var horizon = addDays(today, ahead);
  var away = [], upcoming = [], pending = [];
  (entries || []).forEach(function (e) {
    if (!e || !e.startDate) return;
    var end = e.endDate || e.startDate;
    if (end < today || e.startDate > horizon) return;
    if (opts.team && teamsOf(e).indexOf(opts.team) === -1) return;
    var r = row(e, today);
    if (e.status === 'approved') {
      if (e.startDate <= today) away.push(r); else upcoming.push(r);
    } else if (e.status === 'pending') {
      pending.push(r);
    }
  });
  var byStart = function (a, b) { return a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : a.name.localeCompare(b.name); };
  away.sort(function (a, b) { return a.endDate < b.endDate ? -1 : a.endDate > b.endDate ? 1 : a.name.localeCompare(b.name); });
  upcoming.sort(byStart);
  pending.sort(byStart);
  return { today: today, away: away, upcoming: upcoming, pending: pending };
}

function whenLabel(days) {
  return days === 1 ? 'tomorrow' : 'in ' + days + ' days';
}

// The push: one alert listing who's off today and whose leave starts within `soonDays`.
// null when there's nothing to say, so a quiet day sends nothing.
// fmtDay(iso) → 'Tue 27 Oct'. opts.soonDays (default 7), opts.withPending (office only).
function leaveAlertMessage(ov, fmtDay, opts) {
  opts = opts || {};
  var soon = opts.soonDays == null ? 7 : opts.soonDays;
  var upcoming = ov.upcoming.filter(function (r) { return r.startsIn <= soon; });
  var pending = opts.withPending ? ov.pending.filter(function (r) { return r.startsIn <= soon; }) : [];
  if (!ov.away.length && !upcoming.length && !pending.length) return null;

  var who = function (r) { return r.name + (r.teams.length ? ' (' + r.teams.join('/') + ')' : ''); };
  var span = function (r) { return r.days === 1 ? fmtDay(r.startDate) : fmtDay(r.startDate) + ' → ' + fmtDay(r.endDate); };
  var lines = [];
  ov.away.forEach(function (r) {
    lines.push('OFF TODAY: ' + who(r) + ' — ' + r.type + (r.endDate === ov.today ? ', back next shift' : ' until ' + fmtDay(r.endDate)));
  });
  upcoming.forEach(function (r) {
    lines.push('Starts ' + whenLabel(r.startsIn) + ': ' + who(r) + ' — ' + r.type + ', ' + span(r));
  });
  pending.forEach(function (r) {
    lines.push('NOT DECIDED: ' + who(r) + ' asked for ' + span(r));
  });

  var title;
  if (ov.away.length === 1) title = ov.away[0].name + ' is on leave today';
  else if (ov.away.length > 1) title = ov.away.length + ' people on leave today';
  else if (upcoming.length) title = 'Leave coming up: ' + upcoming[0].name + ' ' + whenLabel(upcoming[0].startsIn);
  else title = pending.length + ' leave request' + (pending.length === 1 ? '' : 's') + ' starting soon, not decided';
  return { title: title, body: lines.join('\n') };
}

module.exports = { leaveOverview, leaveAlertMessage, teamsOf, addDays, daysBetween };
